/**
 * Composite pass (art §3.1 [B], §4.10 frames, §4.12 post, §4.13 impact frames) → the swapchain, premultiplied
 * (GDD C130): panel shake → ink bleed → frame SDF + band stack + spandrel ornament + transparent bleed + frame-break →
 * static grain → aged vignette / burn (picture interior) → impact frame → grade + knee tonemap → sRGB + dither → ×α.
 */
import * as d from 'typegpu/data';
import * as std from 'typegpu/std';
import tgpu from 'typegpu';
import { postLayout } from '../gpu/layouts';
import { fbm2o, knee, linearToSrgb3, luma, rnd2, sdArch, sdBox, sdCircle, sdQuatrefoil, sdTrefoil, vnoise } from './lib';
import { leaf2d } from './scene';
import {
  P_CHAR, P_GOLD_LIGHT, P_INK, P_INK_DEEP, P_LAPIS, P_LAPIS_LIGHT, P_LEAD_WHITE, P_MINIUM, P_RUBRIC, P_VERMILION,
} from './pigments';

export const fullscreenVertex = tgpu.vertexFn({
  in: { vid: d.builtin.vertexIndex },
  out: { position: d.builtin.position, uv: d.vec2f },
})((input) => {
  'use gpu';
  const p = [d.vec2f(-1, -1), d.vec2f(3, -1), d.vec2f(-1, 3)];
  const xy = p[input.vid];
  return { position: d.vec4f(xy, 0, 1), uv: d.vec2f(xy.x * 0.5 + 0.5, 0.5 - xy.y * 0.5) };
});

const rot2 = (p: d.v2f, a: number) => {
  'use gpu';
  const c = std.cos(a);
  const s = std.sin(a);
  return d.vec2f(p.x * c - p.y * s, p.x * s + p.y * c);
};

const sampleScene = (p: d.v2f) => {
  'use gpu';
  const P = postLayout.$.post;
  return std.textureSampleLevel(postLayout.$.scene, postLayout.$.samp, p.div(P.canvas.xy), 0);
};

/** AA'd window [a, b] of u. */
const win = (u: number, a: number, b: number, aa: number) => {
  'use gpu';
  return std.smoothstep(a - aa, a + aa, u) - std.smoothstep(b - aa, b + aa, u);
};

/** Picture-interior SDF (device px, negative inside) for the frame kinds of art §4.10. */
const pictureSdf = (p: d.v2f, kind: number, k: number) => {
  'use gpu';
  const P = postLayout.$.post;
  const pic = P.picture;
  const w = pic.z * 0.5;
  const cx = pic.x + w;
  const rect = sdBox(p.sub(d.vec2f(cx, pic.y + pic.w * 0.5)), d.vec2f(w, pic.w * 0.5));
  let dd = rect;
  if (kind === 2 || kind === 3) {
    const kk = std.select(k, d.f32(0), kind === 3);
    const rise = std.min(w * std.sqrt(1.0 + 2.0 * kk), pic.w * 0.6);
    const q = d.vec2f(p.x - cx, pic.y + rise - p.y);
    dd = std.max(sdArch(q, w, kk, pic.w - rise), rect);
  } else if (kind === 4) {
    const rise = w;
    const q = d.vec2f(p.x - cx, pic.y + rise - p.y);
    dd = std.max(sdTrefoil(q, w, pic.w - rise), rect);
  }
  return dd;
};

export const compositeFragment = tgpu.fragmentFn({
  in: { uv: d.vec2f, fragPos: d.builtin.position },
  out: d.vec4f,
})((input) => {
  'use gpu';
  const P = postLayout.$.post;
  const px = input.fragPos.xy;
  const s = P.frame.w;
  const dpr = P.view.y;
  const unit = std.max(s * dpr, 0.5); // device px per band unit (CSS px at s = 1)
  const time = P.fx.x;
  const view = d.u32(P.view.x);

  // panel shake: picture and frame move together inside the bleed
  const pc = P.panel.xy.add(P.panel.zw.mul(0.5));
  const p = rot2(px.sub(pc).sub(P.shake.xy), -P.shake.z).add(pc);

  let sc = sampleScene(p);
  // triptych sub-panels (art §7.3): a frozen band shows its held pixels; the gaps between bands are the dividers
  const subN = d.u32(P.subFlags.x);
  let gap = d.f32(-1);
  if (subN > 0) {
    let nearest = d.f32(1e9);
    let band = d.i32(-1);
    for (let i = 0; i < 3; i++) {
      if (d.u32(i) < subN) {
        const r = P.sub[i];
        const db = sdBox(p.sub(r.xy.add(r.zw.mul(0.5))), r.zw.mul(0.5));
        if (db < 0.0) { band = i; }
        nearest = std.min(nearest, std.max(db, 0.0));
      }
    }
    const frozenBits = d.u32(P.subFlags.y);
    if (band >= 0 && ((frozenBits >>> d.u32(band)) & 1) === 1) {
      const held = std.textureSampleLevel(postLayout.$.held, postLayout.$.samp, p.div(P.canvas.xy), 0);
      sc = std.mix(sc, held, P.subFlags.w);
    }
    if (band < 0) { gap = nearest; }
  }
  let col = sc.xyz;
  const fig = sc.w;

  // ink bleed along the fibres (Med+): only dark ink spreads
  if (P.view.w > 0.5) {
    const o = d.vec2f(vnoise(px.mul(0.35)) - 0.5, vnoise(px.mul(0.35).add(d.vec2f(7.1, 3.3))) - 0.5).mul(d.vec2f(1.6, 0.6)).mul(dpr);
    const t1 = sampleScene(p.add(o)).xyz;
    const t2 = sampleScene(p.sub(d.vec2f(o.y, o.x))).xyz;
    const dk = std.select(t2, t1, luma(t1) < luma(t2));
    col = std.mix(col, dk, std.smoothstep(0.03, 0.006, luma(dk)) * 0.5 * std.step(luma(dk), luma(col)));
  }

  const kind = d.u32(P.frame.x);
  const dPic = pictureSdf(p, kind, P.frame.y);
  const dOut = sdBox(p.sub(pc), P.panel.zw.mul(0.5));
  const aaPx = 0.8;
  const u = dPic / unit; //  band units from the picture edge (outwards)
  const o = -dOut / unit; // band units from the outer edge (inwards)
  const aaU = aaPx / unit;
  let alpha = d.f32(1);
  let frameCol = d.vec3f(0);
  const nz = vnoise(px.mul(0.09 / dpr));

  if (kind !== 0) {
    // ── band stack (outside → inside): ink 1.5 · vermilion 9 + pearls · ink 1.5 · gold 3 · ink 1 = 16 ──
    const verm = std.mix(P_RUBRIC.$, P_VERMILION.$, 0.65 + (nz - 0.5) * 0.5 + (fbm2o(px.mul(0.02 / dpr)) - 0.5) * 0.3);
    frameCol = d.vec3f(verm);
    // gold fillet with a painted bevel: lighter on the inner edge
    const gE = 0.5 + 0.25 * std.sin(p.x * 0.004 / dpr + p.y * 0.006 / dpr) + (std.saturate((u - 1.0) / 3.0) - 0.5) * -0.35;
    const g = leaf2d(p.div(dpr), gE, time, d.f32(6));
    frameCol = std.mix(frameCol, g, win(u, d.f32(1), d.f32(4), aaU));
    const inkL = std.max(std.max(win(u, -0.2, d.f32(1), aaU), win(u, d.f32(4), 5.5, aaU)), win(u, 14.5, 16.2, aaU));
    // pearls: lead-white dots every 7 units along the vermilion band centre (u = 10)
    const pic = P.picture;
    const w = pic.z * 0.5;
    const cx = pic.x + w;
    const kk = std.select(P.frame.y, d.f32(0), kind === 3);
    const rise = std.select(std.min(w * std.sqrt(1.0 + 2.0 * kk), pic.w * 0.6), w, kind === 4);
    const qy = pic.y + std.select(d.f32(0), rise, kind === 2 || kind === 3 || kind === 4) - p.y;
    const qx = p.x - cx;
    let along = std.select(qx, qy, std.abs(qx) > w - 2.0 * unit && qy > -(pic.w - std.select(d.f32(0), rise, kind >= 2)) + 2.0 * unit);
    if ((kind === 2 || kind === 3) && qy > 0.0) {
      const R = w * (1.0 + kk);
      along = std.atan2(qy, std.abs(qx) + kk * w) * R;
    }
    const pa = std.fract(along / (7.0 * unit) + 0.5) - 0.5;
    const pearl = 1.0 - std.smoothstep(1.4 - aaU, 1.4 + aaU, std.length(d.vec2f(pa * 7.0, u - 10.0)));
    frameCol = std.mix(frameCol, P_LEAD_WHITE.$, pearl * std.step(5.6, u) * (1.0 - std.step(14.4, u)));
    frameCol = std.mix(frameCol, P_INK.$, inkL);

    // ── spandrels (arch frames): vermilion diaper with gilt quatrefoil rosettes, a keystone medallion ──
    if (u > 15.0) {
      const sp = std.mix(P_RUBRIC.$, P_VERMILION.$, 0.55 + (nz - 0.5) * 0.4);
      const cell = 24.0 * unit;
      const lp = p.sub(d.vec2f(cx, P.panel.y)).div(cell);
      const lc = std.fract(lp).sub(d.vec2f(0.5));
      const qf = sdQuatrefoil(lc, 0.16, 0.15);
      const qfK = 1.0 - std.smoothstep(-0.02, 0.02, qf);
      let spc = std.mix(sp, leaf2d(p.div(dpr), 0.62 + lc.y * 0.5, time, d.f32(6)), qfK);
      spc = std.mix(spc, P_INK.$, (1.0 - std.smoothstep(0.0, 0.045, std.abs(qf))) * 0.9);
      // lapis lozenge field behind the rosettes (rule of tincture: metal on colour)
      const lz = std.abs(lc.x) + std.abs(lc.y);
      spc = std.mix(spc, std.mix(P_LAPIS.$, P_LAPIS_LIGHT.$, nz * 0.6), (1.0 - std.smoothstep(0.47, 0.5, lz)) * std.step(0.25, lz) * (1.0 - qfK) * 0.0);
      const dotL = 1.0 - std.smoothstep(0.05, 0.08, std.length(std.abs(lc).sub(d.vec2f(0.5, 0.5))));
      spc = std.mix(spc, P_GOLD_LIGHT.$, dotL * 0.9);
      // keystone medallion top-centre
      const kc = d.vec2f(cx, P.panel.y + std.max(rise * 0.5, 14.0 * unit));
      const km = sdCircle(p.sub(kc), 13.0 * unit);
      const kq = sdQuatrefoil(p.sub(kc).div(unit * 13.0), 0.4, 0.42);
      spc = std.mix(spc, std.mix(P_LAPIS.$, P_LAPIS_LIGHT.$, nz * 0.5), 1.0 - std.smoothstep(-aaPx, aaPx, km));
      spc = std.mix(spc, leaf2d(p.div(dpr), 0.6 + (p.y - kc.y) / (unit * 40.0), time, d.f32(6)), 1.0 - std.smoothstep(-0.03, 0.03, kq));
      spc = std.mix(spc, P_INK.$, (1.0 - std.smoothstep(0.0, 0.05, std.abs(kq))) * 0.9);
      spc = std.mix(spc, P_INK.$, 1.0 - std.smoothstep(0.0, 1.4 * unit, std.abs(km)));
      spc = std.mix(spc, leaf2d(p.div(dpr), 0.7, time, d.f32(6)), win(km / unit, 1.4, 3.6, aaU));
      frameCol = std.mix(frameCol, spc, std.smoothstep(16.0 - aaU, 16.0 + aaU, u));
      // outer edge of the panel: ink 1.5 + gold 2.5 + ink 1
      const outer = win(o, d.f32(-1), 1.5, aaU) + win(o, d.f32(4), d.f32(5), aaU);
      const og = leaf2d(p.div(dpr), 0.55 + (nz - 0.5) * 0.3, time, d.f32(6));
      frameCol = std.mix(frameCol, og, win(o, 1.5, d.f32(4), aaU) * std.smoothstep(16.0, 17.0, u));
      frameCol = std.mix(frameCol, P_INK.$, std.saturate(outer) * std.smoothstep(16.0, 17.0, u));
    }
    const inFrame = std.smoothstep(-aaPx, aaPx, dPic);
    col = std.mix(col, frameCol, inFrame);
    // the transparent bleed ring outside the panel (the DOM vellum shows through)
    alpha = 1.0 - std.smoothstep(-aaPx, aaPx, dOut);
    // frame-break: flagged figures paint over the band and into the bleed by their coverage
    if (fig > 0.7) {
      const kf = std.saturate((fig - 0.5) * 2.0);
      col = std.mix(col, sc.xyz, kf * std.step(0.0, dPic));
      alpha = std.max(alpha, kf);
    }
  }

  // triptych dividers: ink 1 · gold 4 · ink 1 (× s), drawn in left → right over drawDividersMs, dissolved with the bands
  if (gap >= 0.0 && dPic < 0.0) {
    const e = gap / unit;
    const along = std.saturate((p.x - P.picture.x) / std.max(P.picture.z, 1.0));
    const shown = std.step(along, P.subFlags.z) * P.subFlags.w;
    const gd = leaf2d(p.div(dpr), 0.6 + (along - 0.5) * 0.3, time, d.f32(6));
    let dc = std.mix(gd, P_INK.$, 1.0 - std.smoothstep(0.8, 1.2, e));
    dc = std.mix(dc, gd, std.smoothstep(0.8, 1.2, e));
    col = std.mix(col, dc, shown);
  }

  // static grain (picture and band), midtone-weighted
  const l0 = luma(col);
  const mid = std.saturate(4.0 * std.sqrt(l0) * (1.0 - std.sqrt(l0)));
  const gr = vnoise(px.mul(0.55)) * 0.55 + vnoise(px.mul(1.7).add(d.vec2f(31, 7))) * 0.25 + rnd2(px) * 0.2;
  col = col.mul(1.0 + (gr - 0.5) * 2.0 * P.fx.y * (0.4 + 0.6 * mid));

  // aged vignette + burn inside the picture interior (distance to the picture edge, not the screen)
  const minSide = std.max(std.min(P.picture.z, P.picture.w), 1.0);
  const v = -dPic / minSide + (fbm2o(px.div(140.0 * dpr)) - 0.5) * 0.04;
  if (dPic < 0.0 && kind !== 0) {
    col = col.mul(std.mix(1.0 - 0.18 * P.fx.w, 1.0, std.smoothstep(0.0, 0.18, v)));
    const burn = P.fx.z;
    if (burn > 0.0) {
      const b = burn * 0.08;
      col = std.mix(col, P_CHAR.$, std.smoothstep(b, b - 0.01, v));
      const ember = (1.0 - std.smoothstep(0.0, 0.006, std.abs(v - b))) * (0.75 + 0.25 * std.sin(time * 37.7));
      col = std.mix(col, P_MINIUM.$, ember);
    }
    // vignette pulse (flash fallback / bell toll)
    const pulse = P.impact.z;
    const pt = std.mix(d.vec3f(0.0), P_VERMILION.$.mul(0.3), P.impact.w);
    col = std.mix(col, pt, pulse * (1.0 - std.smoothstep(0.0, 0.25, v)));
    // impact frames (art §4.13): negative then white
    const imp = d.u32(P.impact.x);
    if (imp === 1) {
      const figure = fig > 0.25;
      const lineish = luma(col) < 0.15;
      const ground = std.select(P_INK_DEEP.$, P_VERMILION.$, P.impact.y > 0.5);
      col = std.select(ground, std.select(P_LEAD_WHITE.$, P_INK_DEEP.$, lineish), figure);
    } else if (imp === 2) {
      col = std.mix(col, P_LEAD_WHITE.$, 0.35);
    }
  }

  // grade + knee tonemap (flat palette colours stay exact below 0.8)
  col = col.mul(P.grade.x);
  col = d.vec3f(col.x * P.grade.y, col.y, col.z);
  const lg = luma(col);
  col = std.mix(d.vec3f(lg), col, P.grade.z);
  col = std.mix(col, col.mul(d.vec3f(0.92, 0.96, 1.1)), P.grade.w * (1.0 - std.saturate(lg * 3.0)));
  col = knee(col, 0.8);

  if (view === 9) {
    col = d.vec3f(sc.xyz);
    alpha = 1.0;
  } else if (view === 5) {
    col = d.vec3f(fig);
    alpha = 1.0;
  } else if (view === 6) {
    col = d.vec3f(alpha);
    alpha = 1.0;
  }

  // sRGB encode + ±0.5/255 dither, premultiply by the output alpha
  const enc = linearToSrgb3(std.saturate(col)).add(d.vec3f((rnd2(px.add(d.vec2f(17, 71))) - 0.5) / 255.0));
  return d.vec4f(enc.mul(alpha), alpha);
});
